/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from './services/api';
import { applyMask } from './utils/masks';
import Header from './components/Header';
import { Footer } from './components/Footer';
import { postToApp } from './utils/inApp';

const ChangeCard: React.FC = () => {
  // `/alterar-cartao/:token` (JWT no caminho, links já enviados) ou o link
  // curto `/cartao/:code` (#623), que vira token aqui antes do formulário.
  const { token: tokenDoLink, code } = useParams<{ token?: string; code?: string }>();
  const [secureToken, setSecureToken] = useState<string | undefined>(tokenDoLink);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    let cancelado = false;
    api
      .get(`/payments/change-card/link/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (cancelado) return;
        const token = (data?.data ?? data)?.token;
        if (token) setSecureToken(token);
        else setLinkError('Não foi possível abrir o link. Tente novamente em instantes.');
      })
      .catch((error: any) => {
        if (cancelado) return;
        const status = error?.response?.status;
        setLinkError(
          status === 404 || status === 410
            ? error?.response?.data?.message ||
                'Este link expirou. Por favor, solicite um novo link de troca de cartão.'
            : 'Não foi possível abrir o link. Tente novamente em instantes.',
        );
      });
    return () => {
      cancelado = true;
    };
  }, [code]);

  const [form, setForm] = useState<{
    cardNumber: string;
    validity: string;
    cvv: string;
    nameOnCard: string;
  }>({
    cardNumber: '',
    validity: '',
    cvv: '',
    nameOnCard: '',
  });

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (field: keyof typeof form, value: string) => {
    let masked = value;
    if (field === 'cardNumber') masked = applyMask(value, 'card');
    else if (field === 'validity') masked = applyMask(value, 'validity');
    else if (field === 'cvv') masked = applyMask(value, 'cvv');
    setForm((prev) => ({ ...prev, [field]: masked }));
  };

  const isFormValid =
    form.cardNumber.replace(/\D/g, '').length >= 15 &&
    /^((0[1-9])|(1[0-2]))\/\d{2,4}$/.test(form.validity) &&
    (form.cvv.length === 3 || form.cvv.length === 4) &&
    form.nameOnCard.trim().length > 3;

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!secureToken) return alert('Token de segurança inválido.');
    if (submitted) return;
    setSubmitted(true);
    setLoading(true);
    setFeedback(null);

    const [monthStr, yearStr] = form.validity.split('/');
    const month = parseInt(monthStr) || 0;
    const rawYear = parseInt(yearStr) || 0;
    const year = rawYear < 100 ? 2000 + rawYear : rawYear;

    try {
      const tokenResponse = await api.post('/payments/tokenize', {
        name: form.nameOnCard,
        number: form.cardNumber.replace(/\s/g, ''),
        cvv: form.cvv,
        month,
        year,
      });
      const token = tokenResponse.data.data.token;

      const response = await api.post('/payments/change-card', {
        token,
        month,
        year: year % 100,
        cvv: form.cvv,
        secureToken,
      });

      setSuccess(true);
      const charged = Boolean(response.data?.data?.charged);
      if (charged) {
        setFeedback('Cartão atualizado e cobrança realizada com sucesso!');
      } else {
        setFeedback('Cartão atualizado com sucesso!');
      }
      // Dentro do app (ChangeCardWebViewScreen): confirmação programática (#133)
      // — antes o app só sabia do sucesso pelo usuário ler a página e voltar.
      // No navegador a bridge não existe e a chamada é no-op.
      postToApp({ type: 'card-changed', charged });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || '';
      if (msg.includes('expirado') || msg.includes('inválido')) {
        setFeedback('Este link expirou. Por favor, solicite um novo link de troca de cartão.');
      } else {
        setFeedback(msg || 'Erro ao atualizar o cartão. Tente novamente.');
      }
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f5]">
      {/* Blue Banner */}
      <div className="w-full bg-[#018ED5] py-3 text-center text-white font-medium text-sm">
        Você está a um passo de aproxímar seus filhos de Deus
      </div>

      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="flex-1 w-full flex items-start justify-center py-8 px-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-md p-8">
          <h1 className="text-2xl font-bold mb-2 text-center text-gray-800">Atualizar Cartão de Crédito</h1>
          <p className="text-center text-gray-600 mb-6">Digite os novos dados do seu cartão de crédito</p>

          {linkError && (
            <div className="mb-6 text-center text-sm font-medium p-4 rounded-lg border text-red-700 bg-red-50 border-red-300">
              {linkError}
            </div>
          )}

          {feedback && (
            <div
              className={`mb-6 text-center text-sm font-medium p-4 rounded-lg border ${
                success ? 'text-green-700 bg-green-50 border-green-300' : 'text-red-700 bg-red-50 border-red-300'
              }`}
            >
              {feedback}
            </div>
          )}

          {!success && !linkError && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número do cartão</label>
                <input
                  type="text"
                  placeholder="0000 0000 0000 0000"
                  inputMode="numeric"
                  maxLength={19}
                  className="border border-gray-300 w-full px-4 py-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.cardNumber}
                  onChange={(e) => handleChange('cardNumber', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Validade</label>
                  <input
                    type="text"
                    placeholder="MM/AAAA"
                    inputMode="numeric"
                    maxLength={7}
                    className="border border-gray-300 w-full px-4 py-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.validity}
                    onChange={(e) => handleChange('validity', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                  <input
                    type="text"
                    placeholder="000"
                    inputMode="numeric"
                    maxLength={4}
                    className="border border-gray-300 w-full px-4 py-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.cvv}
                    onChange={(e) => handleChange('cvv', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome impresso no cartão</label>
                <input
                  type="text"
                  placeholder="Nome e Sobrenome"
                  className="border border-gray-300 w-full px-4 py-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.nameOnCard}
                  onChange={(e) => handleChange('nameOnCard', e.target.value)}
                />
              </div>

              <div className="pt-4">
                <button
                  type="button"
                  disabled={!isFormValid || loading || submitted || !secureToken}
                  onClick={handleSubmit}
                  className={`w-full py-4 text-white rounded-lg font-semibold text-lg transition-all ${
                    !isFormValid || loading || submitted || !secureToken
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#018ED5] hover:bg-[#0179b8] shadow-md hover:shadow-lg'
                  }`}
                >
                  {loading ? 'Atualizando...' : submitted ? 'Cartão atualizado' : 'Atualizar cartão'}
                </button>
              </div>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-green-700 font-medium">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  SEUS DADOS ESTÃO SEGUROS
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ChangeCard;
